/* Search drawer: shows results while the shopper types.
   <cs-live-search> sits inside the drawer's form. It asks the store for /search/suggest with the
   "predictive-search" section (Section Rendering API), swaps the returned HTML in, and switches between the
   idle menu, a loading skeleton and the results. Without JavaScript the form just opens the search page. */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.dialog = this.closest('dialog');
    this.input = this.form?.querySelector('input[type="search"]');
    this.clearButton = this.form?.querySelector('[type="reset"]');
    this.idle = this.querySelector('[data-state="idle"]');
    this.results = this.querySelector('[data-state="results"]');
    this.loading = this.querySelector('[data-state="loading"]');
    this.enabled = this.dataset.enabled === 'true';
    this.cache = new Map();
    if (!this.form || !this.input) return;

    this.onInput = () => {
      this.toggleClear();
      this.debouncedSearch();
    };
    this.debouncedSearch = window.Theme.debounce(() => this.search(), 250);
    this.onReset = () => setTimeout(() => { this.toggleClear(); this.search(); this.input.focus(); });
    this.onOpen = (event) => { if (event.detail.dialog === this.dialog) setTimeout(() => this.input.focus(), 60); };
    this.onClick = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) this.selectTab(tab);
    };
    this.onKey = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const tabs = [...tab.parentElement.querySelectorAll('[role="tab"]')];
      const next = tabs[(tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      this.selectTab(next);
      next.focus();
    };

    // Escape in a search field only clears it in Chrome; here it closes the drawer, as on the live store.
    this.onEscape = (event) => {
      if (event.key !== 'Escape' || !this.dialog) return;
      event.preventDefault();
      window.Theme.closeDialog?.(this.dialog);
    };

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onEscape);
    this.form.addEventListener('reset', this.onReset);
    document.addEventListener('dialog:open', this.onOpen);
    this.results.addEventListener('click', this.onClick);
    this.results.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('keydown', this.onEscape);
    this.form?.removeEventListener('reset', this.onReset);
    document.removeEventListener('dialog:open', this.onOpen);
    this.controller?.abort();
  }

  toggleClear() {
    if (this.clearButton) this.clearButton.hidden = this.input.value === '';
  }

  show(state) {
    this.idle.hidden = state !== 'idle';
    this.results.hidden = state !== 'results';
    this.loading.hidden = state !== 'loading';
  }

  selectTab(tab) {
    const list = tab.closest('[role="tablist"]');
    list.querySelectorAll('[role="tab"]').forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
  }

  async search() {
    const query = this.input.value.trim();
    this.controller?.abort();
    if (!query || !this.enabled) {
      this.show('idle');
      return;
    }
    if (this.cache.has(query)) {
      this.render(this.cache.get(query));
      return;
    }

    this.controller = new AbortController();
    if (this.results.hidden) this.show('loading');
    else this.results.classList.add('is-updating');

    try {
      const url = new URL(window.Theme.config.routes.predictiveSearch, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', 'product,collection,article,page,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Search failed (${response.status})`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = doc.querySelector('[data-predictive-root]');
      const html = root ? root.outerHTML : '';
      this.cache.set(query, html);
      this.render(html);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.show('idle'); // the form still works: Enter opens the search page
    }
  }

  render(html) {
    this.results.innerHTML = html;
    this.results.classList.remove('is-updating');
    this.show(html ? 'results' : 'idle');
  }
}
if (!customElements.get('cs-live-search')) customElements.define('cs-live-search', PredictiveSearch);
